import { useMemo } from 'react';
import { Badge, Spinner } from 'react-bootstrap';
import AdvanceTable from '../base/AdvanceTable';
import AdvanceTableFooter from '../base/AdvanceTableFooter';
import AdvanceTableProvider from '../../providers/AdvanceTableProvider';
import useAdvanceTable from '../../hooks/useAdvanceTable';
import PeerDelta from './PeerDelta.js';

const fmtKm = m =>
  m == null ? '—' : m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`;

const fmtDur = sec => {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
};

const PHASE_LABEL = {
  unwinding: 'Unwinding',
  pre_stranding: 'Pre-stranding',
  conductor_isolation: 'Isolation',
  rope: 'Rope',
  interior_sheat: 'Inner Sheath',
  armouring: 'Armouring',
  exterior_sheat: 'Outer Sheath',
  coiling: 'Coiling',
  rewinding: 'Rewinding'
};
const phaseText = r => {
  const base = PHASE_LABEL[r.phase] || r.phase || '—';
  return r.tandem ? `${base} + Inner (tandem)` : base;
};

const columnsFor = onRowClick => [
  {
    accessorKey: 'operator',
    header: 'Operator',
    cell: ({ row: { original } }) =>
      onRowClick ? (
        <button
          className="btn btn-link p-0 fw-semibold text-start"
          // a number nobody can check is not trustworthy, just unarguable
          title="Show the drums behind this number"
          onClick={() => onRowClick(original)}
        >
          {original.operator}
        </button>
      ) : (
        <span className="fw-semibold text-900">{original.operator}</span>
      ),
    meta: { headerProps: { style: { width: '14%' } }, cellProps: { className: 'py-2' } }
  },
  {
    accessorKey: 'productLabel',
    header: 'Product',
    cell: ({ row: { original } }) => (
      <div className="d-flex flex-column">
        <span className="fw-semibold">{original.productLabel || '—'}</span>
        {original.section ? (
          <span className="fs-10 text-700">{original.section} mm²</span>
        ) : null}
      </div>
    ),
    meta: { headerProps: { style: { width: '16%' } }, cellProps: { className: 'py-2' } }
  },
  {
    id: 'phase',
    accessorFn: r => phaseText(r),
    header: 'Phase',
    cell: ({ row: { original } }) => (
      <div className="d-flex align-items-center gap-1 flex-wrap">
        <span>{PHASE_LABEL[original.phase] || original.phase || '—'}</span>
        {/* A tandem run extrudes two layers in one pass — same phase name, twice
            the work, so it is never averaged in with a plain run. */}
        {original.tandem ? (
          <Badge bg="info" variant="phoenix" className="fs-10" title="Interior + exterior in one pass">
            tandem
          </Badge>
        ) : null}
      </div>
    ),
    meta: { headerProps: { style: { width: '14%' } } }
  },
  {
    accessorKey: 'machineId',
    header: 'Machine',
    cell: ({ row: { original } }) => (
      <span className="text-uppercase text-700 fs-10">{original.machineId || '—'}</span>
    ),
    meta: { headerProps: { style: { width: '8%' } } }
  },
  {
    accessorKey: 'meters',
    header: 'Produced',
    cell: ({ row: { original } }) => (
      <div className="d-flex flex-column text-end">
        <span className="fw-semibold">{fmtKm(original.meters)}</span>
        <span className="fs-10 text-700">
          {original.tokens} {original.tokens === 1 ? 'drum' : 'drums'}
        </span>
      </div>
    ),
    meta: {
      headerProps: { style: { width: '10%' }, className: 'text-end' },
      cellProps: { className: 'text-end' }
    }
  },
  {
    accessorKey: 'runningSec',
    header: 'Running',
    cell: ({ row: { original } }) => (
      <span className="text-700">{fmtDur(original.runningSec)}</span>
    ),
    meta: {
      headerProps: { style: { width: '8%' }, className: 'text-end' },
      cellProps: { className: 'text-end' }
    }
  },
  {
    accessorKey: 'speed',
    header: 'Speed',
    cell: ({ row: { original } }) => (
      <span className="fw-bold text-primary">
        {original.speed != null ? `${original.speed} m/min` : '—'}
      </span>
    ),
    meta: {
      headerProps: { style: { width: '10%' }, className: 'text-end' },
      cellProps: { className: 'text-end' }
    }
  },
  {
    id: 'peer',
    accessorFn: r => r.deltaPct ?? -9999,
    header: 'vs peers',
    cell: ({ row: { original } }) => (
      <PeerDelta
        delta={original.deltaPct}
        peers={original.peerOperators}
        peerValue={original.peerMedianSpeed}
        unit=" m/min"
        higherIsBetter
      />
    ),
    meta: {
      headerProps: { style: { width: '10%' }, className: 'text-end' },
      cellProps: { className: 'text-end' }
    }
  },
  {
    id: 'best',
    header: 'Best here',
    enableSorting: false,
    cell: ({ row: { original } }) =>
      original.peerBestSpeed != null ? (
        <span
          className={`fs-10 ${original.speed === original.peerBestSpeed ? 'text-success fw-semibold' : 'text-700'}`}
          title={`Fastest anyone has run this: ${original.peerBestSpeed} m/min`}
        >
          {original.peerBestSpeed} m/min
          {original.speed === original.peerBestSpeed ? ' ★' : ''}
        </span>
      ) : (
        <span className="text-700">—</span>
      ),
    meta: {
      headerProps: { style: { width: '10%' }, className: 'text-end' },
      cellProps: { className: 'text-end' }
    }
  }
];

/**
 * Operator × product × phase, with each row measured against everyone else who
 * ran the same work. Comparing overall speeds would be noise: one operator may
 * run thin wire on a fast line all week while another runs armoured cable.
 */
const OperatorMatrixTable = ({ rows = [], isLoading, onRowClick }) => {
  const data = useMemo(() => rows, [rows]);

  const table = useAdvanceTable({
    data,
    columns: useMemo(() => columnsFor(onRowClick), [onRowClick]),
    pageSize: 15,
    pagination: true,
    sortable: true
  });

  if (isLoading) {
    return (
      <div className="d-flex align-items-center justify-content-center py-5 gap-2">
        <Spinner animation="border" size="sm" variant="primary" />
        <span className="text-700 fs-9">Loading…</span>
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="text-center py-5 text-700 fs-9">
        No production recorded for these filters yet.
      </div>
    );
  }

  return (
    <AdvanceTableProvider {...table}>
      <AdvanceTable tableProps={{ className: 'phoenix-table border-top border-200 fs-9' }} />
      <AdvanceTableFooter pagination className="py-3" />
    </AdvanceTableProvider>
  );
};

export default OperatorMatrixTable;
